import { useEffect, useState } from 'react';
import { z } from 'zod';
import { api } from '@/utils/api';
import { useAuthStore } from '@/stores/auth';

const money = z.number().finite().nonnegative();
const person = z.object({ source: z.string().nullable().optional(), firstName: z.string().nullable().optional(), lastName: z.string().nullable().optional(), city: z.string().nullable().optional(), phone: z.string().nullable().optional(), mobile: z.string().nullable().optional() });
const event = z.object({ id: z.string(), at: z.string().datetime(), kind: z.string(), name: z.string().nullable(), to: z.enum(['/follow-ups', '/sales/activities', '/telecaller']) });
const daySchema = z.object({
  callsToday: z.number().int().nonnegative(), followupsDue: z.number().int().nonnegative(), overdue: z.number().int().nonnegative(),
  due: z.array(event), today: z.array(event),
  proposals: z.array(z.object({ id: z.string(), number: z.string(), customer: z.string().nullable(), amount: money.nullable(), status: z.string(), forManager: z.boolean() })),
  won: z.object({ count: z.number().int().nonnegative(), amount: money.nullable() }),
});
const queueSchema = z.array(z.object({ id: z.string(), priority: z.number(), lastCalledAt: z.string().nullable(),
  contact: person.nullable(), campaign: z.object({ name: z.string() }).nullable(), deal: z.object({ source: z.string().nullable() }).nullable(),
}));
const outstandingSchema = z.object({ rows: z.array(z.object({
  id: z.string(), number: z.string(), customer: z.string().nullable(), orderDate: z.string().datetime(),
  amount: money, paid: money, balance: money.positive(), currency: z.literal('INR'),
})), total: money, currency: z.literal('INR') }).superRefine((data, ctx) => {
  if (Math.round(data.total * 100) !== data.rows.reduce((sum, r) => sum + Math.round(r.balance * 100), 0)) ctx.addIssue({ code: 'custom', message: 'Outstanding total does not match balances' });
});
export type MyDay = z.infer<typeof daySchema>;
export type Queue = z.infer<typeof queueSchema>;
export type Outstanding = z.infer<typeof outstandingSchema>;
export const rupees = (n: number) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 2 }).format(n);
export const time = (iso: string) => new Date(iso).toLocaleTimeString('en-IN', { timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit' });

export function usePersonalQuery<T>(path: string, parse: (response: any) => T) {
  const user = useAuthStore(s => s.user);
  const identity = `${user?.tenantId ?? ''}:${user?.id ?? ''}`;
  const [attempt, setAttempt] = useState(0);
  const [state, setState] = useState<{ identity: string; data?: T; error?: string }>({ identity });
  useEffect(() => {
    let active = true;
    setState({ identity });
    api.get(path).then(response => {
      if (response.data?.success !== true) throw new Error('Request failed');
      const data = parse(response.data);
      if (active) setState({ identity, data });
    }).catch(() => { if (active) setState({ identity, error: 'Could not load data. Retry to check again.' }); });
    return () => { active = false; };
  }, [path, identity, attempt, parse]);
  return { ...(state.identity === identity ? state : { identity }), retry: () => setAttempt(n => n + 1) };
}
const parseDay = (r: any) => ({ queue: queueSchema.parse(r.data), day: daySchema.parse(r.meta?.myDay) });
const parseOutstanding = (r: any) => outstandingSchema.parse(r.data);
export const useMyDay = () => usePersonalQuery('/telecaller/queue?view=my-day&limit=50', parseDay);
export const useMyOutstanding = () => usePersonalQuery('/my-outstanding', parseOutstanding);

const parseQueue = (r: any) => queueSchema.parse(r.data);
export const useCallQueue = () => usePersonalQuery('/telecaller/queue?limit=50', parseQueue);
